<template>
    <div class="charges-editor-page">
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <md-card class="custom-card">
                    <md-card-content class="body-list">
                        <div
                            :class="[{ fixed: isFixedSelector }]"
                            class="bulk-section sticky-selection"
                            v-if="selectedJobs.length > 1"
                        >
                            <div>{{ selectedJobs.length }} job{{ selectedJobs.length > 1 ? 's' : '' }} selected.</div>
                            <div>
                                <md-button
                                    class="md-round md-just-icon md-success"
                                    title="Recalculate jobs"
                                    @click="bulkRecalculate"
                                >
                                    <md-icon>currency_exchange</md-icon>
                                </md-button>
                                <md-button
                                    class="md-round md-just-icon md-success"
                                    title="Approve jobs"
                                    @click="bulkApprove"
                                >
                                    <md-icon>thumb_up</md-icon>
                                </md-button>
                                <md-button
                                    class="md-round md-just-icon md-danger"
                                    title="Unapprove jobs"
                                    @click="bulkUnapprove"
                                >
                                    <md-icon>thumb_down</md-icon>
                                </md-button>
                            </div>
                        </div>
                        <ProcessChargesTable>
                            <template v-slot:table-header>
                                <md-checkbox v-model="isSelectAll" @change="onSelectAll(isSelectAll)"></md-checkbox>
                                <table-header-dropdown
                                    class="reference-column"
                                    column-icon="keyboard_arrow_down"
                                    :default-text="tableColumnOptions.columns[columnKeys.referenceSource].name"
                                    :dropdown-options="tableColumnKeyOptions.referenceSource"
                                    :selected-option="tableColumnOptions.columns[columnKeys.referenceSource]"
                                    @selectedOption="
                                        $_handleColumnSwitch(
                                            $event,
                                            columnKeys.referenceSource,
                                            'stops.defaults.stop-table-options'
                                        )
                                    "
                                />
                                <h5>Trip Date</h5>
                                <h5>Customer</h5>
                                <h5>Service Pkg</h5>
                                <h5 class="time-header">Time</h5>
                                <h5 class="hide-column">Attempt #</h5>
                                <h5 class="hide-column">Run #</h5>
                                <h5>Status</h5>
                                <h5>Delivered By</h5>
                                <table-header-dropdown
                                    class="finance-column"
                                    default-text="Total Charge"
                                    :dropdown-options="totalChargeDropdownList"
                                    :selected-option="selectedTotalChargeDropdowns"
                                    @selectedOption="handleSelectTotalChargeFilter"
                                />
                                <table-header-dropdown
                                    class="finance-column"
                                    default-text="Total Cost"
                                    :dropdown-options="totalCostDropdownList"
                                    :selected-option="selectedTotalCostDropdowns"
                                    @selectedOption="handleSelectTotalCostFilter"
                                />
                                <div class="finance-column hide-column">
                                    <h5>Margin</h5>
                                </div>
                                <h5>Actions</h5>
                            </template>
                            <template
                                v-slot:table-content
                                v-if="
                                    jobChargeList !== null &&
                                        jobChargeList !== undefined &&
                                        jobChargeList.length > 0 &&
                                        !isLoading
                                "
                            >
                                <div v-for="(item, index) in jobChargeList" :key="index">
                                    <ActiveRow
                                        :root-job="item"
                                        :selected-jobs="selectedJobs"
                                        :has-expanded-row="item.hasExpandedRow"
                                        :reference-header-value="tableColumnOptions.columns[columnKeys.referenceSource]"
                                        :is-cost-approval-feature-enabled="isCostApprovalFeatureEnabled"
                                        :is-charge-approval-feature-enabled="isChargeApprovalFeatureEnabled"
                                        :service-packages="servicePackages"
                                        @row-click="handleRowClick(index)"
                                        @update-rates="updateRates(item)"
                                        @approve-jobs="approveJobs(item)"
                                        @unapprove-jobs="unapproveJobs(item)"
                                        @update-service-package="updateServicePackage(item)"
                                        @update-time="handleTimeUpdated"
                                    >
                                        <template v-slot:nested-table v-if="item.childrenJobs.length > 0">
                                            <NestedTable
                                                :root-job="item"
                                                :selected-jobs="selectedJobs"
                                                :reference-header-value="
                                                    tableColumnOptions.columns[columnKeys.referenceSource]
                                                "
                                                @update-rates="updateRates"
                                                @select-items="handleNestedItemsSelected"
                                                @deselect-items="handleNestedItemsDeselected"
                                            />
                                        </template>
                                    </ActiveRow>
                                </div>
                            </template>
                            <template v-else v-slot:table-content>
                                <div v-if="isLoading" class="content-loader loading-position">
                                    <fade-loader :loading="isLoading" color="#333333" />
                                    <span>LOADING</span>
                                </div>
                                <p v-else class="no-result-message">
                                    No results matching your search/filter could be found.
                                </p>
                            </template>
                        </ProcessChargesTable>
                    </md-card-content>
                </md-card>
            </div>
        </div>
        <md-card-actions class="page-footer" md-alignment="space-between">
            <div>
                <p v-if="total === pagination.perPage" class="card-category">
                    Page {{ pagination.currentPage }} of many
                </p>
                <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>

                <p>Total Jobs: {{ pagination.total }}</p>
            </div>
            <pagination
                v-model="pagination.currentPage"
                class="pagination-no-border pagination-success"
                :per-page="pagination.perPage"
                :total="total"
                @change-page="handleChangePage($event, pagination.perPage)"
            />
        </md-card-actions>

        <div class="progress-container" v-if="showProgressBox">
            <div class="button-close">
                <md-button class="md-default md-just-icon md-round" @click="handleCloseProgressBox">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
            <div class="progress-text-section" v-if="!isProgressComplete">
                <div>
                    <div class="btn-loader route-loader"></div>
                </div>
                <div v-if="totalToRecalculate == 0">Preparing...</div>
                <div v-else>{{ currentProcessed }} of {{ totalToRecalculate }} have been recalculated.</div>
            </div>
            <div v-else class="progress-text-section">
                <div>
                    <md-icon class="green-check">check_circle</md-icon>
                </div>
                <div>Recalculated Jobs.</div>
            </div>
        </div>
    </div>
</template>

<script>
import { PAGINATION_DEFAULTS, STOP_TABLE_HEADER_DISPLAY_OPTION } from '@/utils/defaults';
import { Pagination, TableHeaderDropdown } from '@/components';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { FeatureManager } from '@/directives';
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import {
    PROCESS_TOTALS_CONSTANTS,
    REFERENCE_SOURCE_KEY_OPTIONS,
    FEATURE_NAMES
} from '@/utils/constants';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { NestedTable, ProcessChargesTable, ActiveRow } from './components';

export default {
    name: 'ProcessChargesShipments',
    components: {
        NestedTable,
        ActiveRow,
        ProcessChargesTable,
        Pagination,
        FadeLoader,
        TableHeaderDropdown
    },
    mixins: [GeneralMixin, FeatureManager],
    props: {
        filter: {
            type: Object,
            default: null
        },
        servicePackages: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isLoading: false,
            isSelectAll: false,
            jobChargeList: [],
            selectedJobs: [],
            pagination: PAGINATION_DEFAULTS,
            fromDateFilterValue: '',
            toDateFilterValue: '',
            expandedRow: null,
            newRow: null,
            loadCapacityList: [],
            selectedRow: null,
            isRecalculating: false,
            shouldCloseDropdown: false,
            filters: null,
            totalCostDropdownList: PROCESS_TOTALS_CONSTANTS,
            selectedTotalCostDropdowns: PROCESS_TOTALS_CONSTANTS[0],
            totalChargeDropdownList: PROCESS_TOTALS_CONSTANTS,
            selectedTotalChargeDropdowns: PROCESS_TOTALS_CONSTANTS[0],
            showProgressBox: false,
            totalToRecalculate: 0,
            currentProcessed: 0,
            isProgressComplete: false,
            isFixedSelector: false,
            isCostApprovalFeatureEnabled: false,
            isChargeApprovalFeatureEnabled: false,
            headerDropdown: REFERENCE_SOURCE_KEY_OPTIONS,
            ...STOP_TABLE_HEADER_DISPLAY_OPTION()
        };
    },
    computed: {
        currentPageMax() {
            const highBound = this.currentPageMin + this.pagination.perPage;
            return this.total < highBound ? this.total : highBound;
        },
        currentPageMin() {
            return this.pagination.perPage * (this.pagination.currentPage - 1);
        },
        total() {
            return this.pagination.total;
        },
        totalPages() {
            if (this.total > 0) {
                return Math.ceil(this.total / this.pagination.perPage);
            }
            return 1;
        },
        ...mapGetters({
            user: 'user/user',
            isReadOnlyUser: 'user/isReadOnlyUser'
        })
    },
    async created() {
        this.$_applyColumnSwitchDefaults('stops.defaults.stop-table-options', {
            [this.columnKeys.referenceSource]: REFERENCE_SOURCE_KEY_OPTIONS
        });
    },
    watch: {
        filter(newVal, oldVal) {
            this.handleFilterChanged(this.filter);
        }
    },
    async mounted() {
        this.filters = this.filter;
        this.loadCapacityList = this.user.vehicleCapacityUnitsConfiguration || [];

        await this.loadJobs();

        document.querySelector('.main-panel').addEventListener('scroll', this.handleScroll, true);

        this.isCostApprovalFeatureEnabled = this.$_hasFeature(FEATURE_NAMES.JobsCostApproval);
        this.isChargeApprovalFeatureEnabled = this.$_hasFeature(FEATURE_NAMES.JobsChargeApproval);
    },
    beforeDestroy() {
        document.querySelector('.main-panel').removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        /**
         * Check if the job matches the identifiers.
         * Running time: O(n) where n is the number of identifiers
         * @param job
         * @param identifiers
         * @returns {*|boolean}
         */
        checkIfJobMatches(job, identifiers) {
            const stopIds = identifiers.filter((i) => i.identifierType === 'stopId').map((i) => i.identifier);

            if (job.stopId) {
                return stopIds.includes(job.stopId);
            }
            return false;
        },
        /**
         *
         * @param {{ identifierType: "stopId" | "shipmentId" | "tripId", identifier: number }[]} items
         */
        handleNestedItemsDeselected(items) {
            const jobsFlattened = [];
            // eslint-disable-next-line no-restricted-syntax
            for (const job of this.jobChargeList) {
                jobsFlattened.push(job);
                jobsFlattened.push(...job.childrenJobs);
            }
            const jobsToDeselect = jobsFlattened.filter((job) => this.checkIfJobMatches(job, items));
            this.selectedJobs = this.selectedJobs.filter((job) => !jobsToDeselect.includes(job));
        },
        /**
         *
         * @param {{ identifierType: "stopId" | "shipmentId" | "tripId", identifier: number }[]} items
         */
        handleNestedItemsSelected(items) {
            // since there are childrenJobs, we need to flatten the list
            const jobsFlattened = [];

            // eslint-disable-next-line no-restricted-syntax
            for (const job of this.jobChargeList) {
                jobsFlattened.push(job);
                jobsFlattened.push(...job.childrenJobs);
            }
            // if an item in the list is already selected, we should remove it
            const newlySelectedJobs = jobsFlattened.filter((job) => this.checkIfJobMatches(job, items));
            // 1. get the intersection of selectedJobs and this.selectedJobs
            // 2. remove the intersection from this.selectedJobs and selectedJobs
            // 3. add the remaining selectedJobs to this.selectedJobs
            const intersection = this.selectedJobs.filter((job) => newlySelectedJobs.includes(job));
            const remainingSelectedJobs = newlySelectedJobs.filter((job) => !intersection.includes(job));
            this.selectedJobs = [...this.selectedJobs, ...remainingSelectedJobs];
        },
        handleTimeUpdated(payload) {
            const {
                tripId,
                newTimes: { actualStartTime, actualEndTime },
                shipmentId
            } = payload;

            if (tripId) {
                const trip = this.jobChargeList.find((t) => t.tripId === tripId);
                trip.actualStartTime = actualStartTime;
                trip.actualEndTime = actualEndTime;
            }

            if (shipmentId) {
                const shipment = this.jobChargeList.find((s) => s.shipmentId === shipmentId);
                shipment.actualStartTime = actualStartTime;
                shipment.actualEndTime = actualEndTime;
            }
        },
        async fetchAllCompletedJobs(pageNumber = 1, itemsPerPage = 50) {
            this.isLoading = true;
            try {
                const endpoint = `/api/process-charges/list/shipments`;
                const response = await handleRequests(endpoint, {
                    params: {
                        pageNumber,
                        itemsPerPage,
                        ...this.filters
                    }
                });
                return response;
            } catch (error) {
                const message = `Cannot fetch jobs, please contact support if issue persists`;
                showErrorMessage(this, message, error);
                return { data: { jobs: [], totalJobs: 0 } };
            } finally {
                this.isLoading = false;
            }
        },
        async handleFetchAllJobs(pageNumber = 1, perPage = 50, clearSelectedJobs = true) {
            if (this.isLoading) {
                return;
            }

            this.$_handleLoaderState(true);
            this.pagination.currentPage = pageNumber;
            this.pagination.perPage = perPage;

            const {
                data: { jobs, totalJobs }
            } = await this.fetchAllCompletedJobs(pageNumber, perPage);

            if (clearSelectedJobs) {
                this.selectedJobs = [];
                this.selectedRow = null;
                this.expandedRow = null;
            }

            this.assignJobcharges(jobs, totalJobs);

            this.$router.replace({ path: this.$route.path, query: { pageNumber } });
            this.$_handleLoaderState(false);
        },
        async loadJobs(pageNumber = 1, perPage = 50) {
            await this.handleFetchAllJobs(pageNumber, perPage, true);
        },
        assignJobcharges(jobs, totalJobs) {
            this.jobChargeList = jobs;
            this.pagination.total = totalJobs;
        },
        onSelectAll(value) {
            if (this.expandedRow) {
                this.removeOpenedRow();
            }

            this.selectedJobs = [];

            if (value) {
                this.jobChargeList.forEach((job) => {
                    this.selectedJobs.push(job);
                });
            } else {
                this.selectedJobs = [];
                this.shouldCloseDropdown = true;
            }
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            this.handleFetchAllJobs(currentPage, perPage, false);
        },
        removeOpenedRow() {
            if (this.selectedRow && this.expandedRow) {
                this.jobChargeList[this.jobChargeList.indexOf(this.selectedRow)].hasExpandedRow = false;
            }

            this.selectedJobs = [];
            this.shouldCloseDropdown = true;
            this.expandedRow = null;
        },
        handleRowClick(index) {
            const updatedIndex = index;

            // only one row can be expanded at a time
            if (this.selectedRow && this.expandedRow) {
                const selectedIndex = this.jobChargeList.indexOf(this.selectedRow);
                this.jobChargeList[this.jobChargeList.indexOf(this.selectedRow)].hasExpandedRow = false;

                this.selectedJobs.splice(this.selectedRow);

                if (selectedIndex === index) {
                    this.expandedRow = null;
                    return;
                }
            }

            this.jobChargeList[updatedIndex].hasExpandedRow = true;
            const row = this.jobChargeList[updatedIndex];

            const objCopy = Object.assign({}, row);
            objCopy.isExpanded = true;
            objCopy.hasExpandedRow = false;

            this.selectedRow = this.jobChargeList[updatedIndex];
            this.expandedRow = objCopy;

            this.selectedJobs.push(this.selectedRow);
        },
        async bulkRecalculate() {
            if (!this.isRecalculating) {
                await this.updateRates();
            }
        },
        async updateRates(item) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Recalculate',
                    body: item
                        ? `Are you sure you want to recalculate ${item.ref}?`
                        : `Are you sure you want to recalculated all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.isRecalculating = true;
                        this.showProgressBox = true;
                        this.totalToRecalculate = 0;
                        this.currentProcessed = 0;
                        this.isProgressComplete = false;

                        const shipmentIds = [];
                        let createBatch = false;

                        if (item) {
                            if (item.shipmentId) {
                                if (item.rateGroupId) {
                                    shipmentIds.push(item.shipmentId);
                                    item.isRecalculating = true;
                                    item.hasExpandedRow = false;
                                }
                            }
                        } else {
                            this.selectedJobs.forEach((item) => {
                                if (item.shipmentId) {
                                    if (item.rateGroupId) {
                                        shipmentIds.push(item.shipmentId);
                                        item.isRecalculating = true;
                                        item.hasExpandedRow = false;
                                        createBatch = true;
                                    }
                                }
                            });
                        }

                        this.removeOpenedRow();

                        this.isSelectAll = false;
                        const { teamId } = this.user;

                        const toUpdate = {
                            shipmentIds,
                            teamId
                        };

                        const payload = {
                            method: 'post',
                            data: toUpdate
                        };

                        if (shipmentIds.length <= 0) {
                            this.isRecalculating = false;
                            this.isProgressComplete = true;
                            return;
                        }

                        const api = createBatch ? `/api/rates/recalculate-rates-Batch` : `/api/rates/recalculate-rates`;
                        try {
                            const response = await handleRequests(api, payload);

                            if (response !== null && response.data !== null && response.status === 200) {
                                if (item) {
                                    item.isRecalculating = false;
                                }
                                this.jobChargeList.forEach((item) => {
                                    if (item.shipmentId && item.isRecalculating) {
                                        item.isRecalculating = false;
                                    }
                                });
                            }

                        } catch (e) {
                            const message = `Cannot recalculate, please contact support if issue persists`;
                            showErrorMessage(this, message, e);
                        }
                    }

                    this.isRecalculating = false;
                });
        },
        async bulkApprove() {
            await this.approveJobs();
        },
        async approveJobs(item) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Approve',
                    body: item
                        ? `Are you sure you want to approve ${item.ref}?`
                        : `Are you sure you want to approve all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    const approvalPayload = this.getApprovalPayload(item);
                    if (
                        approvalPayload.stopIds.length <= 0 &&
                        approvalPayload.shipmentIds.length <= 0 &&
                        approvalPayload.tripIds.length <= 0
                    ) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data: approvalPayload
                    };

                    this.$_handleLoaderState(true, 'APPROVING...');

                    const api = `/api/process-charges/approve-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(item ? `Job ${item.ref} approved.` : 'Jobs approved.');
                        this.$_handleLoaderState(false);

                        this.selectedJobs = [];
                    } catch (e) {
                        const message = `Cannot approve, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                        this.$_handleLoaderState(false);
                    }

                    this.removeOpenedRow();
                    this.isSelectAll = false;
                });
        },
        async bulkUnapprove() {
            await this.unapproveJobs();
        },
        async unapproveJobs(item) {
            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Unapprove',
                    body: item
                        ? `Are you sure you want to unapprove ${item.ref}?`
                        : `Are you sure you want to unapprove all job/s?`,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response !== 'Confirm') {
                        return;
                    }

                    const approvalPayload = this.getApprovalPayload(item);
                    if (
                        approvalPayload.stopIds.length <= 0 &&
                        approvalPayload.shipmentIds.length <= 0 &&
                        approvalPayload.tripIds.length <= 0
                    ) {
                        return;
                    }

                    const payload = {
                        method: 'post',
                        data: approvalPayload
                    };

                    this.$_handleLoaderState(true, 'UNAPPROVING...');

                    const api = `/api/process-charges/unapprove-jobs`;
                    try {
                        await handleRequests(api, payload);

                        this.$notifySuccess(item ? `Job ${item.ref} unapproved.` : 'Jobs unapproved.');
                        this.$_handleLoaderState(false);

                        this.selectedJobs = [];
                    } catch (e) {
                        const message = `Cannot unapprove, please contact support if issue persists`;
                        showErrorMessage(this, message, e);
                        this.$_handleLoaderState(false);
                    }

                    this.removeOpenedRow();

                    this.isSelectAll = false;
                });
        },
        getApprovalPayload(item) {
            const stopIds = [];
            const shipmentIds = [];
            const tripIds = [];

            if (item) {
                item.hasExpandedRow = false;

                if (item.stopId) {
                    stopIds.push(item.stopId);
                }
                if (item.shipmentId) {
                    shipmentIds.push(item.shipmentId);
                }
                if (item.tripId) {
                    tripIds.push(item.tripId);
                }
            } else {
                this.selectedJobs.forEach((item) => {
                    item.hasExpandedRow = false;

                    if (item.stopId) {
                        stopIds.push(item.stopId);
                    }
                    if (item.shipmentId) {
                        shipmentIds.push(item.shipmentId);
                    }
                    if (item.tripId) {
                        tripIds.push(item.tripId);
                    }
                });
            }

            return {
                stopIds,
                shipmentIds,
                tripIds
            };
        },
        async handleSelectTotalCostFilter(item) {
            this.selectedTotalCostDropdowns = item;
            const updateFilters = { ...this.filters, totalCostFilter: item.key };

            await this.handleFilterChanged(updateFilters);
        },
        async handleSelectTotalChargeFilter(item) {
            this.selectedTotalChargeDropdowns = item;
            const updateFilters = { ...this.filters, totalChargeFilter: item.key };

            await this.handleFilterChanged(updateFilters);
        },
        async handleFilterChanged(data) {
            this.filters = Object.assign(this.filters, data);
            this.loadJobs();
        },
        handleRecalculationCompleted(totalSuccess, failedRecords) {
            if (Number(failedRecords) <= 0) {
                this.isProgressComplete = true;
            } else {
                this.isProgressComplete = true;
                this.showProgressBox = false;

                this.$notify({
                    message: `Completed: ${totalSuccess} Failed  ${failedRecords}.<br /><br /> Partially recalculated, please try again. If error persists please contact support.`,
                    type: 'warning',
                    duration: 10000
                });
            }
        },
        async handleShipmentRatesUpdated(shipmentId, updateCosts, updateCharges) {
            const shipment = this.jobChargeList.find((s) => Number(shipmentId) === s.shipmentId);

            if (shipment) {
                await this.fetchShipment(shipmentId, updateCosts, updateCharges);
            }
        },
        handleShipmentRecalculated(shipmentId, updateCosts, updateCharges) {
            const shipment = this.jobChargeList.find((s) => Number(shipmentId) === s.shipmentId);

            if (shipment) {
                this.fetchShipment(shipmentId, updateCosts, updateCharges);
            }

            this.currentProcessed = this.currentProcessed + 1;
        },
        handleTotalToBeRecalculated(total) {
            this.totalToRecalculate = Number(total);
        },
        handleCloseProgressBox() {
            this.showProgressBox = false;
        },
        async fetchShipment(shipmentId, updateCosts, updateCharges) {
            const endpoint = `/api/process-charges/shipment/${shipmentId}`;
            const response = await handleRequests(endpoint);
            const shipment = response.data;
            this.removeOpenedRow();

            for (let i = 0; i < this.jobChargeList.length; i++) {
                if (this.jobChargeList[i].shipmentId === shipment.shipmentId) {
                    this.jobChargeList.splice(i, 1, shipment);
                    break;
                }
            }

            this.toggleHighlight(shipment, updateCosts, updateCharges);
        },
        handleRecalculationFailed() {
            this.showProgressBox = false;
            showErrorMessage(this, 'Failed to recalculate jobs, if error persists please contact support.', null);
        },
        handleScroll() {
            const target = document.querySelector('.md-card-header');
            const scrollPos = target.getBoundingClientRect().top;

            if (scrollPos <= 80) {
                this.isFixedSelector = true;
                return;
            }

            this.isFixedSelector = false;
        },
        toggleHighlight(job, updateCosts, updateCharges) {
            if (updateCosts) {
                job.hasUpdatedCosts = Boolean(updateCosts);
                setTimeout(() => {
                    job.hasUpdatedCosts = false;
                }, 1000);
            }

            if (updateCharges) {
                job.hasUpdatedCharges = Boolean(updateCharges);
                setTimeout(() => {
                    job.hasUpdatedCharges = false;
                }, 1000);
            }
        },
        async onShipmentApprovalStatusUpdated(shipmentId, approved, unapproved) {
            const shipmentIndex = this.jobChargeList.findIndex((j) => j.shipmentId === Number(shipmentId));

            if (this.filters.approvalStatus === 'Unapproved' && approved) {
                this.jobChargeList.splice(shipmentIndex, 1);
            } else if (this.filters.approvalStatus === 'Approved' && unapproved) {
                this.jobChargeList.splice(shipmentIndex, 1);
            } else {
                await this.fetchShipment(Number(shipmentId));
            }
        },
        async updateServicePackage(item) {
            const data = this.setIsRecalculating(item, true);

            const payload = {
                method: 'patch',
                data
            };

            const endpoint = `/api/process-charges/update-service`;
            try {
                await handleRequests(endpoint, payload);
            } catch (e) {
                const message = `Cannot update service packages.`;
                showErrorMessage(this, message, e);
            }
        },
        setIsRecalculating(item, value) {
            const shipmentIds = [];

            item.isRecalculating = value;

            if (item.shipmentId) {
                shipmentIds.push(item.shipmentId);
            }

            const toUpdate = {
                rateGroupId: item.rateGroupId ?? null,
                shipmentIds
            };

            this.isRecalculating = true;
            this.showProgressBox = true;
            this.totalToRecalculate = 0;
            this.currentProcessed = 0;
            this.isProgressComplete = false;

            return toUpdate;
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .md-table-head-label {
    color: #4caf50 !important;
}

.hide-container {
    display: none;
}

@media (min-width: 1400px) {
    .hide-container {
        display: table-cell;
        line-height: 30px;

        ::v-deep .md-table-cell-container {
            max-width: 300px;
        }
    }
}

.custom-button-container .custom-a-blue a {
    color: rgba(0, 0, 0, 0.87);
}

.custom-card {
    z-index: initial;
}

.custom-tab-list {
    position: initial;
}

.status-filter {
    width: 250px !important;
    padding-right: 15px;
}

.more_button {
    position: absolute;
}

.selected-row {
    background-color: aliceblue !important;
}

.margin-icon {
    color: red !important;
}

.warming-icon {
    color: orange !important;
}

.warning-font {
    color: red !important;
}

.profile-image {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    margin-right: 5px;
    object-fit: contain;
    background-color: #eee;
}

.tooltip-width {
    max-width: 500px;
}

.address-text-tooltip {
    text-align: left;
}

.address-separator {
    float: left;
    margin-right: 10px;
    font-weight: bold;
}

.address-text {
    white-space: nowrap;
    width: 46%;
    overflow: hidden;
    text-overflow: ellipsis;
    float: left;
}

.stop-address-text {
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    float: left;
}

.body-list {
    position: relative;
}

.bulk-section {
    position: absolute;
    top: -35px;
    width: 545px;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    text-align: center;
    background-color: #cfeefa;
    color: #2b93ff;
    font-weight: 400;
    padding: 5px 10px 5px 20px;

    > div {
        width: 60%;
        display: inline-block;
        vertical-align: middle;
        text-align: left;
    }

    > div:last-child {
        width: 40%;
        text-align: right;

        button {
            height: 30px;
            width: 30px;
            min-width: 30px;
        }

        .dropdown {
            width: 120px;
            background-color: #ff9800 !important;
            display: inline-block;
            text-align: center;
            color: #fff;
            text-transform: uppercase;
            font-size: 12px;
            height: 30px;
            line-height: 30px;
            margin-top: 6px;
            cursor: pointer;
        }
    }
}

.progress-container {
    min-width: 200px;
    max-width: 250px;
    background-color: #fff;
    position: fixed;
    right: 12px;
    bottom: 8px;
    z-index: 99;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
    padding: 0 10px 10px 10px;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;

    .progress-text-section {
        display: table;
        padding-top: 12px;

        > div {
            display: table-cell;
            vertical-align: middle;
        }

        > div:last-child {
            padding-left: 10px;
        }
    }

    ::v-deep .md-button {
        height: 20px;
        min-width: 20px;
        width: 20px;
        position: absolute;
        top: -12px;
        right: -12px;

        i {
            font-size: 15px !important;
        }
    }
}

.green-check {
    color: #4caf50 !important;
}

.btn-loader {
    border: 4px solid #eeeeee;
    border-radius: 50%;
    border-top: 4px solid #2b93ff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite;
    animation: spin 1s linear infinite;
    top: 85%;
    left: 48%;
}

@-webkit-keyframes spin {
    0% {
        -webkit-transform: rotate(0deg);
    }

    100% {
        -webkit-transform: rotate(360deg);
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.route-loader {
    top: 20% !important;
    left: 0 !important;
}

.route-loader-text {
    left: 37px;
    position: absolute;
    top: 13px;
}

.sync-loader {
    display: inline-block;
    background-color: #2b93ff;
    border-radius: 50%;
    width: 27px;
    height: 27px;

    svg {
        margin-top: 3px;
        margin-left: 2px;
    }
}

.has-invoice,
.is-approved {
    display: inline-block !important;
    border-radius: 50% !important;
    width: 25px;
    height: 25px;
}

.center-data {
    display: flex !important;
    align-items: center !important;
}

.sticky-selection {
    z-index: 1000;
    transition: top 0.1s ease;
}

.sticky-selection.fixed {
    position: sticky !important;
    top: 70px !important;
}

.cost-updated {
    display: flex !important;
    align-items: center !important;
    color: #4caf50 !important;
    font-weight: bold;
}

.finance-column {
    justify-self: end;
}

.reference-column {
    top: 0px !important;
    padding-left: 0px;
    width: 170px;
}

h5 {
    margin: 0;
}

.loading-position {
    left: 50%;
    top: 65%;
}

.time-header {
    min-width: 100px;
}

.icon-display {
    display: flex !important;
}

::v-deep .running-load-head-table-header .running-load-menu .dropdown {
    top: -2px !important;
}
</style>
